/**
 * "Your tickets shipped" — the On Prod tickets from `deployed[]` that still
 * ask something of Yevhen, kept until marked Seen (A7Y-4733, A7Y-5873): each
 * says what it asks (`waitingOn`: assigned to him, his re-check, or a comment
 * that asks him). Carried items are flagged. An item disappears once its key
 * is in `overview.suppressedKeys` (the server's `isHelmItemSuppressed`
 * verdict) — optimistically the moment an action fires, and for real once
 * the next run's overlay agrees.
 */
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import { HELM_SHIPPED_ASKS } from '@a7y/platform-health/helm';
import type { HelmActionInput, HelmDeployedItem } from '@a7y/platform-health/helm';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';

/** What a shipped ticket asks of Yevhen, the words the card prints. A run doc from
 * before `waitingOn` existed carries none, and then no reason is shown; `roster`
 * is a run stored before A7Y-5873. */
const WAITING_ON_LABEL: Record<NonNullable<HelmDeployedItem['waitingOn']>, string> = {
  ...HELM_SHIPPED_ASKS,
  roster: 'in your roster',
};

export function ShippedSection({
  items,
  suppressedKeys,
  onAction,
}: {
  items: HelmDeployedItem[];
  suppressedKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
}) {
  const visible = items.filter((item) => !suppressedKeys.has(item.key));

  return (
    <HelmSection title="Your tickets shipped (On Prod, last 14 days)" lineOn="none">
      {visible.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Ticket</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((item) => (
              <TableRow key={item.key} data-testid="helm-shipped-item">
                <TableCell className="min-w-0 break-words">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium hover:underline"
                  >
                    {item.identifier}
                  </a>{' '}
                  <span className="text-zinc-700 dark:text-zinc-300">{item.title}</span>
                </TableCell>
                <TableCell className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge color={item.toState === 'On Prod' ? 'purple' : 'zinc'}>
                      {item.toState}
                    </Badge>
                    {item.waitingOn && (
                      <Badge color="teal" data-testid="helm-shipped-waiting-on">
                        {WAITING_ON_LABEL[item.waitingOn]}
                      </Badge>
                    )}
                    {item.carried && (
                      <span className="text-xs text-zinc-500">
                        carried since {new Date(item.enteredAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <ItemActionsMenu
                    itemKey={item.key}
                    decision={item.decision}
                    onAction={onAction}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}
